/**
 * Aba "Rotas e SEO": os endereços das colunas (a base de cada uma), as rotas que
 * o manifesto gera a partir deles, o SEO do site e o conteúdo de notícia.
 *
 * Um endereço só vale se for único e não estiver dentro de outro: duas colunas
 * não podem disputar a mesma URL. A tela confere isso antes de gravar; o
 * servidor confere de novo.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import { portalKeys, useManifesto, useSalvarSecao } from '@/hooks/usePortal';
import { classificarFalha, errosDeCampo } from '@/services/portal';
import {
  COLUNAS_COM_BASE,
  type ColunaComBase,
  type CorpoSecao,
  type ErroDeCampo,
  type PortalSiteDetalhe,
  type RotaManifesto,
} from '@/types/portal';
import { rotuloDaColuna } from '@/utils/portal';

import { CampoInterruptor, CampoTexto, ListaDeErros } from './campos';
import { CartaoSecao } from './CartaoSecao';
import { EditorConteudo, EditorSeo } from './EditoresBloco';
import styles from './Portal.module.css';
import { validarBase, validarBasesUnicas, validarConteudo, validarSeo } from './validacao';

interface Rascunho {
  base: string;
  sitemap: boolean;
}

interface EstadoLinha {
  conflito: boolean;
  errosServidor: ErroDeCampo[];
  falha: string | null;
  salvo: boolean;
}

const LINHA_LIMPA: EstadoLinha = { conflito: false, errosServidor: [], falha: null, salvo: false };

function salvosDe(detalhe: PortalSiteDetalhe): Record<ColunaComBase, Rascunho> {
  const r = {} as Record<ColunaComBase, Rascunho>;
  for (const c of COLUNAS_COM_BASE) {
    r[c] = { base: detalhe.secoes[c].config.base, sitemap: detalhe.secoes[c].sitemap };
  }
  return r;
}

function versoesDe(detalhe: PortalSiteDetalhe): Record<ColunaComBase, number> {
  const r = {} as Record<ColunaComBase, number>;
  for (const c of COLUNAS_COM_BASE) r[c] = detalhe.secoes[c].versao;
  return r;
}

function EnderecosDasColunas({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const qc = useQueryClient();
  const salvarMut = useSalvarSecao(detalhe.site_id);
  const [rasc, setRasc] = useState(() => salvosDe(detalhe));
  const [vistas, setVistas] = useState(() => versoesDe(detalhe));
  const [linhas, setLinhas] = useState<Partial<Record<ColunaComBase, EstadoLinha>>>({});
  const [salvando, setSalvando] = useState<ColunaComBase | null>(null);

  // Uma coluna mudou por fora (gravou aqui, ou a pessoa recarregou depois de um
  // 412): o rascunho dela volta ao que o servidor tem. As outras seguem como estão.
  const atuais = versoesDe(detalhe);
  const mudadas = COLUNAS_COM_BASE.filter((c) => vistas[c] !== atuais[c]);
  if (mudadas.length > 0) {
    const novos = salvosDe(detalhe);
    setVistas(atuais);
    setRasc((r) => {
      const prox = { ...r };
      for (const c of mudadas) prox[c] = novos[c];
      return prox;
    });
    setLinhas((l) => {
      const prox = { ...l };
      for (const c of mudadas) prox[c] = { ...LINHA_LIMPA, salvo: l[c]?.salvo ?? false };
      return prox;
    });
  }

  const salvos = salvosDe(detalhe);
  const bases = Object.fromEntries(COLUNAS_COM_BASE.map((c) => [c, rasc[c].base.trim()])) as Record<
    ColunaComBase,
    string
  >;
  const cruzados = validarBasesUnicas(bases);

  const errosDaLinha = (c: ColunaComBase): ErroDeCampo[] => [
    ...validarBase(rasc[c].base, 'base'),
    ...(cruzados.get(c) ?? []).map((mensagem) => ({ campo: 'base', mensagem })),
  ];

  const salvar = async (c: ColunaComBase) => {
    const sec = detalhe.secoes[c];
    const corpo = {
      ativa: sec.ativa,
      servida_por: sec.servida_por,
      sitemap: rasc[c].sitemap,
      config: { ...sec.config, base: rasc[c].base.trim() },
    } as CorpoSecao;
    setSalvando(c);
    setLinhas((l) => ({ ...l, [c]: { ...LINHA_LIMPA } }));
    try {
      await salvarMut.mutateAsync({ chave: c, corpo, versao: sec.versao });
      setLinhas((l) => ({ ...l, [c]: { ...LINHA_LIMPA, salvo: true } }));
    } catch (e) {
      const tipo = classificarFalha(e);
      setLinhas((l) => ({
        ...l,
        [c]: {
          ...LINHA_LIMPA,
          conflito: tipo === 'conflito',
          errosServidor: tipo === 'validacao' ? errosDeCampo(e) : [],
          falha:
            tipo === 'outra' || tipo === 'sem_api' || tipo === 'sem_plano'
              ? 'Não foi possível salvar agora. Tente de novo em instantes.'
              : null,
        },
      }));
    } finally {
      setSalvando(null);
    }
  };

  const recarregar = async (c: ColunaComBase) => {
    await qc.refetchQueries({ queryKey: portalKeys.site(detalhe.site_id) });
    setLinhas((l) => ({ ...l, [c]: { ...LINHA_LIMPA } }));
  };

  const hostBase = detalhe.url.replace(/\/+$/, '');

  return (
    <section className={styles.cartao} aria-label="Endereços das colunas">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Endereços das colunas</h3>
          <p className={styles.cartaoResumo}>
            O início do endereço de cada coluna. Mudar um endereço muda as URLs públicas: faça só
            antes de a coluna ser indexada, ou deixe um redirecionamento no tema.
          </p>
        </div>
      </div>
      <div className={styles.cartaoCorpo}>
        <ul className={styles.lista} aria-label="Endereços">
          {COLUNAS_COM_BASE.map((c) => {
            const sec = detalhe.secoes[c];
            const estado = linhas[c] ?? LINHA_LIMPA;
            const erros = [...errosDaLinha(c), ...estado.errosServidor];
            const sujo = rasc[c].base !== salvos[c].base || rasc[c].sitemap !== salvos[c].sitemap;
            const rotulo = rotuloDaColuna(c);
            return (
              <li key={c} className={styles.linha} style={{ gridTemplateColumns: '1fr' }}>
                <div className={styles.cartaoEtiquetas}>
                  <strong className={styles.linhaTitulo}>{rotulo}</strong>
                  {!sec.ativa && (
                    <Badge tone="neutral" size="xs">
                      Desligada
                    </Badge>
                  )}
                  {sec.ativa && sec.servida_por === 'plugin' && (
                    <Badge tone="ok" size="xs">
                      Servida pelo plugin
                    </Badge>
                  )}
                  {sec.ativa && sec.servida_por === 'tema' && (
                    <Badge tone="info" size="xs">
                      Servida pelo tema
                    </Badge>
                  )}
                </div>
                <div className={styles.linhaCampos}>
                  <CampoTexto
                    rotulo={`Endereço de ${rotulo}`}
                    valor={rasc[c].base}
                    mono
                    maxLength={80}
                    dica={`${hostBase}/${rasc[c].base.trim()}/`}
                    erro={erros.find((e) => e.campo === 'base')?.mensagem ?? null}
                    onChange={(base) => setRasc((r) => ({ ...r, [c]: { ...r[c], base } }))}
                  />
                  <CampoInterruptor
                    rotulo={`Sitemap de ${rotulo}`}
                    valor={rasc[c].sitemap}
                    dica="Registra os sitemaps da coluna."
                    onChange={(sitemap) => setRasc((r) => ({ ...r, [c]: { ...r[c], sitemap } }))}
                  />
                </div>
                {estado.conflito && (
                  <div role="alert" className={styles.avisoWarn}>
                    <span className={styles.avisoTitulo}>
                      Outra pessoa alterou esta coluna; recarregue.
                    </span>
                    <div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void recarregar(c)}
                      >
                        Recarregar
                      </Button>
                    </div>
                  </div>
                )}
                {/* O erro de formato e de conflito já aparece no próprio campo; a lista
                    é para o que o servidor recusou e não tem campo na tela. */}
                <ListaDeErros erros={estado.errosServidor} titulo={`Endereço de ${rotulo}`} />
                {estado.falha && (
                  <div role="alert" className={styles.avisoCrit}>
                    {estado.falha}
                  </div>
                )}
                <div className={styles.cartaoRodape}>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!sujo || erros.length > 0 || salvando !== null || estado.conflito}
                    loading={salvando === c}
                    onClick={() => void salvar(c)}
                    aria-label={`Salvar endereço de ${rotulo}`}
                  >
                    Salvar
                  </Button>
                  {sujo && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setRasc((r) => ({ ...r, [c]: salvos[c] }))}
                    >
                      Descartar
                    </Button>
                  )}
                  <span role="status" className={styles.mudo}>
                    {estado.salvo && !sujo ? 'Salvo.' : ''}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function descreverItens(r: RotaManifesto): string {
  const partes = Object.entries(r.itens).map(([nome, p]) =>
    p.tipo === 'lista'
      ? `${nome}: ${p.valores.length} ${p.valores.length === 1 ? 'valor' : 'valores'}`
      : `${nome}: padrão ${p.regex}`,
  );
  return partes.length > 0 ? partes.join(' · ') : 'sem marcador';
}

function RotasGeradas({ siteId }: { siteId: string }) {
  const { data, isLoading, isError } = useManifesto(siteId);
  return (
    <section className={styles.cartao} aria-label="Rotas geradas">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Rotas geradas</h3>
          <p className={styles.cartaoResumo}>
            O que o manifesto entrega ao plugin, a partir das colunas ativas. Endereço fora da lista
            de itens responde 404.
          </p>
        </div>
        {data && (
          <Badge tone="neutral" size="xs">
            manifesto {data.etag.slice(0, 8)}
          </Badge>
        )}
      </div>
      <div className={styles.cartaoCorpo}>
        {isLoading && <p className={styles.mudo}>Carregando as rotas…</p>}
        {isError && (
          <p className={styles.erroCampo}>Não foi possível ler o manifesto resolvido agora.</p>
        )}
        {data && data.rotas.length === 0 && (
          <p className={styles.vazio}>Nenhuma rota: não há coluna ativa com endereço.</p>
        )}
        {data && data.rotas.length > 0 && (
          <div className={styles.rolagem}>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th scope="col">Coluna</th>
                  <th scope="col">Endereço</th>
                  <th scope="col">Resposta</th>
                  <th scope="col">Itens válidos</th>
                </tr>
              </thead>
              <tbody>
                {data.rotas.map((r) => (
                  <tr key={r.id}>
                    <td>{rotuloDaColuna(r.modulo)}</td>
                    <td className={styles.mono}>/{r.padrao}</td>
                    <td>
                      {r.resposta === 'redireciona' ? `301 para /${r.destino ?? ''}` : 'Página'}
                    </td>
                    <td>{descreverItens(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

export function AbaRotasSeo({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  return (
    <div>
      <EnderecosDasColunas detalhe={detalhe} />
      <RotasGeradas siteId={detalhe.site_id} />

      <CartaoSecao
        siteId={detalhe.site_id}
        secao={detalhe.secoes.seo}
        titulo="SEO"
        resumo="Quem emite canonical, description, Open Graph e JSON-LD das páginas do plugin, e como tratar conteúdo perecível."
        validar={validarSeo}
      >
        {(ctx) => <EditorSeo {...ctx} />}
      </CartaoSecao>

      <CartaoSecao
        siteId={detalhe.site_id}
        secao={detalhe.secoes.conteudo}
        titulo="Conteúdo de notícia"
        resumo="Categorias de notícia da home, do feed e do llms.txt, e o filtro de listas sem repetição."
        validar={validarConteudo}
      >
        {(ctx) => <EditorConteudo {...ctx} />}
      </CartaoSecao>
    </div>
  );
}
